<template>
  <div>
    <PageTitleVue> CONFIGURAR LAYOUT </PageTitleVue>

    <section class="space-y-5">
      <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
        >
          <div>
            <button
              type="button"
              class="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-[#2f8f2f]"
              @click="goBack"
            >
              <PhArrowLeft class="h-4 w-4" />
              Voltar para layouts
            </button>

            <div v-if="layout">
              <div class="flex flex-wrap items-center gap-3">
                <h1 class="text-2xl font-bold text-gray-900">
                  {{ layout.name }}
                </h1>

                <span
                  class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold"
                  :class="
                    layout.is_active
                      ? 'border-green-100 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-gray-100 text-gray-500'
                  "
                >
                  {{ layout.is_active ? "Ativo" : "Inativo" }}
                </span>

                <span
                  v-if="layout.is_default"
                  class="inline-flex items-center gap-1 rounded-full border border-yellow-100 bg-yellow-50 px-3 py-1 text-xs font-semibold text-yellow-700"
                >
                  <PhStar class="h-3.5 w-3.5" />
                  Padrão
                </span>
              </div>

              <p class="mt-1 text-sm text-gray-500">
                Configure as áreas dinâmicas da capa usando coordenadas
                proporcionais.
              </p>
            </div>

            <div v-else class="space-y-2">
              <div class="h-7 w-72 animate-pulse rounded-lg bg-gray-100"></div>
              <div class="h-4 w-96 animate-pulse rounded-lg bg-gray-100"></div>
            </div>
          </div>

          <div v-if="layout" class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
              @click="resetCurrentArea"
            >
              <PhArrowCounterClockwise class="h-5 w-5" />
              Resetar área
            </button>

            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="isSaving || !coverSrc"
              @click="saveCurrentArea"
            >
              <PhCircleNotch v-if="isSaving" class="h-5 w-5 animate-spin" />
              <PhCheck v-else class="h-5 w-5" />
              Salvar configuração
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="layout && !coverSrc"
        class="rounded-2xl border border-yellow-100 bg-yellow-50/70 p-4"
      >
        <div class="flex gap-3">
          <div
            class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-yellow-600 shadow-sm"
          >
            <PhWarningCircle class="h-5 w-5" />
          </div>

          <div>
            <h3 class="text-sm font-bold text-yellow-800">
              Capa não configurada
            </h3>

            <p class="mt-1 text-sm text-yellow-700">
              Para posicionar a logo e demais dados na capa, envie primeiro uma
              imagem de capa no cadastro do layout.
            </p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <main class="xl:col-span-8">
          <div
            class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
          >
            <div
              class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <h2 class="text-lg font-bold text-gray-800">Preview da capa</h2>

                <p class="text-sm text-gray-500">
                  As áreas são salvas em percentual para manter a posição em
                  qualquer tamanho de página.
                </p>
              </div>

              <span
                class="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700"
              >
                {{ selectedArea.label }}
              </span>
            </div>

            <div
              v-if="coverSrc"
              class="flex justify-center overflow-auto rounded-2xl border border-gray-100 bg-gray-50 p-4"
            >
              <div
                ref="coverPreviewRef"
                class="relative w-full max-w-[780px] overflow-hidden rounded-xl bg-white shadow-sm"
              >
                <img
                  :src="coverSrc"
                  :alt="layout?.name || 'Capa do layout'"
                  class="block w-full select-none"
                  draggable="false"
                />

                <div
                  v-for="previewArea in visiblePreviewAreas"
                  :key="previewArea.key"
                  class="absolute flex items-center justify-center overflow-visible border-2 border-dashed transition"
                  :class="
                    previewArea.key === selectedAreaKey
                      ? 'border-[#2f8f2f] bg-green-500/10 cursor-move z-20'
                      : 'border-gray-400 bg-white/20 cursor-pointer z-10'
                  "
                  :style="getAreaStyle(previewArea.area)"
                  @pointerdown.stop.prevent="
                    startMoveArea(previewArea.key, $event)
                  "
                >
                  <div
                    class="flex h-full w-full items-center justify-center overflow-hidden"
                    :style="getAreaContentFrameStyle(previewArea)"
                  >
                    <template v-if="previewArea.type === 'image'">
                      <img
                        v-if="testLogoSrc"
                        :src="testLogoSrc"
                        :alt="testClientName"
                        class="pointer-events-none h-full w-full select-none"
                        :style="getImageAreaStyle(previewArea)"
                        draggable="false"
                      />

                      <div
                        v-else
                        class="text-center pointer-events-none select-none"
                      >
                        <PhBuildings class="mx-auto h-8 w-8 text-gray-400" />
                        <p class="mt-1 text-[10px] font-semibold text-gray-500">
                          Logo
                        </p>
                      </div>
                    </template>

                    <template v-else>
                      <span
                        class="line-clamp-3 px-2 pointer-events-none select-none"
                        :style="getTextAreaStyle(previewArea)"
                      >
                        {{ getTextPreview(previewArea.key) }}
                      </span>
                    </template>
                  </div>

                  <template v-if="previewArea.key === selectedAreaKey">
                    <span
                      v-for="handle in resizeHandles"
                      :key="handle.key"
                      class="absolute z-30 h-4 w-4 rounded-full border-2 border-white bg-[#2f8f2f] shadow-lg ring-1 ring-green-700/20"
                      :style="handle.style"
                      @pointerdown.stop.prevent="
                        startResizeArea(previewArea.key, handle.key, $event)
                      "
                    ></span>
                  </template>
                </div>
              </div>
            </div>

            <ReportEmptyState
              v-else
              title="Nenhuma capa para configurar"
              description="Volte ao cadastro do layout e envie uma imagem de capa para iniciar a configuração das áreas dinâmicas."
              :icon="PhImageSquare"
            />
          </div>
        </main>

        <aside class="xl:col-span-4">
          <div class="sticky top-5 space-y-4">
            <div
              class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
            >
              <h3 class="text-base font-bold text-gray-800">Área dinâmica</h3>

              <p class="mt-1 text-sm text-gray-500">
                Escolha qual informação será posicionada sobre a capa.
              </p>

              <div
                v-for="areaOption in editableElements"
                :key="areaOption.key"
                class="flex w-full items-center gap-2"
              >
                <!-- check -->
                <button
                  type="button"
                  class="flex min-w-0 flex-1 items-center justify-between rounded-xl border px-4 py-3 text-left transition"
                  :class="
                    selectedAreaKey === areaOption.key
                      ? 'border-green-100 bg-green-50 text-[#2f8f2f]'
                      : 'border-gray-100 bg-white text-gray-600 hover:bg-gray-50'
                  "
                  @click="selectArea(areaOption.key)"
                >
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold">
                      {{ areaOption.label }}
                    </p>

                    <p class="truncate text-xs text-gray-400">
                      {{ areaOption.description }}
                    </p>
                  </div>

                  <PhCheckCircle
                    v-if="hasSavedArea(areaOption.key)"
                    class="h-5 w-5 shrink-0 text-green-600"
                  />
                </button>

                <!-- eyes -->
                <button
                  type="button"
                  class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition"
                  :class="
                    isAreaVisible(areaOption.key)
                      ? 'border-green-100 bg-green-50 text-green-700 hover:bg-green-100'
                      : 'border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100'
                  "
                  :title="isAreaVisible(areaOption.key) ? 'Ocultar item' : 'Mostrar item'"
                  @click="toggleAreaVisibility(areaOption.key)"
                >
                  <PhEye v-if="isAreaVisible(areaOption.key)" class="h-5 w-5" />
                  <PhEyeSlash v-else class="h-5 w-5" />
                </button>

                <!-- remove custom fields-->
                <button
                  v-if="areaOption.storage === 'custom'"
                  type="button"
                  class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-500 transition hover:bg-red-100"
                  title="Remover campo personalizado"
                  @click="removeCustomField(areaOption.key)"
                >
                  <PhTrash class="h-5 w-5" />
                </button>
              </div>

              <!-- add custom fields -->
              <button
                type="button"
                class="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-green-200 bg-green-50/60 px-4 py-3 text-sm font-semibold text-[#2f8f2f] transition hover:bg-green-50"
                @click="addCustomField"
              >
                <PhPlus class="h-5 w-5" />
                Adicionar campo personalizado
              </button>

            </div>

            <!-- configuração de estilos -->
            <div class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <h3 class="text-base font-bold text-gray-800">
                Estilo do elemento
              </h3>

              <p class="mt-1 text-sm text-gray-500">
                Personalize a aparência do item selecionado na capa.
              </p>

              <label
                class="mt-5 flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-gray-50/70 p-4 text-start"
              >
                <div>
                  <p class="text-sm font-semibold text-gray-800">
                    Exibir na capa
                  </p>

                  <p class="mt-0.5 text-xs text-gray-500">
                    O item ficará salvo, mas poderá ser ocultado do layout.
                  </p>
                </div>

                <input
                  v-model="currentElementConfig.visible"
                  type="checkbox"
                  class="h-5 w-5 rounded border-gray-300 text-[#2f8f2f] focus:ring-green-100"
                />
              </label>

              <!-- customize custom fields -->
              <div
                v-if="isCurrentCustomField && currentCustomField"
                class="mt-5 rounded-2xl border border-green-100 bg-green-50/50 p-4"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h4 class="text-sm font-bold text-gray-800">
                      Campo personalizado
                    </h4>

                    <p class="mt-1 text-xs text-gray-500">
                      Edite o nome interno e o texto que será exibido na capa.
                    </p>
                  </div>

                  <button
                    type="button"
                    class="rounded-lg p-2 text-red-400 transition hover:bg-red-50 hover:text-red-600"
                    title="Remover campo"
                    @click="removeCustomField(currentCustomField.key)"
                  >
                    <PhTrash class="h-5 w-5" />
                  </button>
                </div>

                <div class="mt-4 space-y-4">
                  <div>
                    <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                      Nome do campo
                    </label>

                    <input
                      v-model="currentCustomField.label"
                      type="text"
                      class="editor-input"
                      placeholder="Ex: Título da capa"
                    />
                  </div>

                  <div>
                    <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                      Texto exibido
                    </label>

                    <textarea
                      v-model="currentCustomField.value"
                      rows="3"
                      class="editor-input resize-none"
                      placeholder="Ex: RELATÓRIO DE VISITA"
                    />
                  </div>
                </div>
              </div>

              <div v-if="isCurrentAreaText" class="mt-5 grid grid-cols-1 gap-4">
                <div>
                  <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                    Fonte
                  </label>

                  <select
                    v-model="currentElementStyle.fontFamily"
                    class="editor-input"
                  >
                    <option
                      v-for="font in fontFamilyOptions"
                      :key="font.value"
                      :value="font.value"
                    >
                      {{ font.label }}
                    </option>
                  </select>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                      Tamanho
                    </label>

                    <input
                      v-model.number="currentElementStyle.fontSize"
                      type="number"
                      min="6"
                      max="120"
                      step="1"
                      class="editor-input"
                      @change="normalizeCurrentElementStyle"
                    />
                  </div>

                  <div>
                    <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                      Peso
                    </label>

                    <select
                      v-model="currentElementStyle.fontWeight"
                      class="editor-input"
                    >
                      <option
                        v-for="weight in fontWeightOptions"
                        :key="weight.value"
                        :value="weight.value"
                      >
                        {{ weight.label }}
                      </option>
                    </select>
                  </div>
                </div>

                <div>
                  <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                    Cor do texto
                  </label>

                  <div class="flex gap-2">
                    <input
                      v-model="currentElementStyle.color"
                      type="color"
                      class="h-11 w-14 cursor-pointer rounded-xl border border-gray-200 bg-white p-1"
                    />

                    <input
                      v-model="currentElementStyle.color"
                      type="text"
                      class="editor-input"
                      placeholder="#111827"
                    />
                  </div>
                </div>

                <div>
                  <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                    Alinhamento
                  </label>

                  <select
                    v-model="currentElementStyle.textAlign"
                    class="editor-input"
                  >
                    <option
                      v-for="align in textAlignOptions"
                      :key="align.value"
                      :value="align.value"
                    >
                      {{ align.label }}
                    </option>
                  </select>
                </div>

                <div>
                  <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                    Transformação
                  </label>

                  <select
                    v-model="currentElementStyle.textTransform"
                    class="editor-input"
                  >
                    <option
                      v-for="option in textTransformOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                      Altura de linha
                    </label>

                    <input
                      v-model.number="currentElementStyle.lineHeight"
                      type="number"
                      min="0.7"
                      max="3"
                      step="0.05"
                      class="editor-input"
                      @change="normalizeCurrentElementStyle"
                    />
                  </div>

                  <div>
                    <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                      Espaçamento
                    </label>

                    <input
                      v-model.number="currentElementStyle.letterSpacing"
                      type="number"
                      min="-10"
                      max="40"
                      step="0.5"
                      class="editor-input"
                      @change="normalizeCurrentElementStyle"
                    />
                  </div>
                </div>
              </div>

              <div class="mt-5">
                <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                  Opacidade
                </label>

                <input
                  v-model.number="currentElementStyle.opacity"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  class="w-full accent-[#2f8f2f]"
                  @change="normalizeCurrentElementStyle"
                />

                <p class="mt-1 text-xs text-gray-400">
                  {{ Math.round((currentElementStyle.opacity ?? 1) * 100) }}%
                </p>
              </div>

              <div v-if="isCurrentAreaImage" class="mt-5 grid grid-cols-1 gap-4">
                <div>
                  <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                    Moldura da logo
                  </label>

                  <select
                    v-model="currentElementStyle.shape"
                    class="editor-input"
                    @change="normalizeCurrentElementStyle"
                  >
                    <option
                      v-for="shape in logoShapeOptions"
                      :key="shape.value"
                      :value="shape.value"
                    >
                      {{ shape.label }}
                    </option>
                  </select>
                </div>

                <div>
                  <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                    Ajuste da imagem
                  </label>

                  <select
                    v-model="currentElementStyle.objectFit"
                    class="editor-input"
                    @change="normalizeCurrentElementStyle"
                  >
                    <option
                      v-for="fit in objectFitOptions"
                      :key="fit.value"
                      :value="fit.value"
                    >
                      {{ fit.label }}
                    </option>
                  </select>

                  <p class="mt-1 text-xs text-gray-400">
                    “Conter” mantém a logo inteira. “Preencher” ocupa toda a moldura e pode cortar partes da imagem.
                  </p>
                </div>

                <div>
                  <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                    Espaçamento interno
                  </label>

                  <input
                    v-model.number="currentElementStyle.padding"
                    type="number"
                    min="0"
                    max="80"
                    step="1"
                    class="editor-input"
                    @change="normalizeCurrentElementStyle"
                  />
                </div>

                <div>
                  <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                    Cor de fundo da moldura
                  </label>

                  <div class="flex gap-2">
                    <input
                      v-model="currentElementStyle.backgroundColor"
                      type="color"
                      class="h-11 w-14 cursor-pointer rounded-xl border border-gray-200 bg-white p-1"
                    />

                    <input
                      v-model="currentElementStyle.backgroundColor"
                      type="text"
                      class="editor-input"
                      placeholder="transparent ou #ffffff"
                    />
                  </div>

                  <button
                    type="button"
                    class="mt-2 text-xs font-semibold text-gray-400 transition hover:text-[#2f8f2f]"
                    @click="currentElementStyle.backgroundColor = 'transparent'"
                  >
                    Usar fundo transparente
                  </button>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                      Borda
                    </label>

                    <input
                      v-model.number="currentElementStyle.borderWidth"
                      type="number"
                      min="0"
                      max="40"
                      step="1"
                      class="editor-input"
                      @change="normalizeCurrentElementStyle"
                    />
                  </div>

                  <div>
                    <label class="mb-1.5 block text-sm font-semibold text-gray-700">
                      Cor da borda
                    </label>

                    <input
                      v-model="currentElementStyle.borderColor"
                      type="color"
                      class="h-11 w-full cursor-pointer rounded-xl border border-gray-200 bg-white p-1"
                    />
                  </div>
                </div>

                <div class="rounded-2xl border border-green-100 bg-green-50/60 p-4 text-sm text-green-700">
                  A logo pode ser posicionada parcialmente fora da capa. A área visível será cortada pelo limite do template.
                </div>
              </div>
            </div>

            <!-- experimentar com a empresa -->
            <div
              class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
            >
              <h3 class="text-base font-bold text-gray-800">
                Testar layout com empresa
              </h3>

              <p class="mt-1 text-sm text-gray-500">
                Use uma empresa cadastrada para simular a capa final.
              </p>

              <select v-model="selectedClientId" class="editor-input mt-4">
                <option value="">Selecione uma empresa</option>

                <option
                  v-for="client in clients"
                  :key="client.id"
                  :value="client.id"
                >
                  {{ client.trade_name || client.corporate_name }}
                </option>
              </select>

              <div
                class="mt-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-sm"
              >
                <div class="flex justify-between gap-4">
                  <span class="text-gray-400">Empresa</span>
                  <span class="text-right font-medium text-gray-700">
                    {{ testClientName }}
                  </span>
                </div>

                <div class="mt-3 flex justify-between gap-4">
                  <span class="text-gray-400">CNPJ</span>
                  <span class="text-right font-medium text-gray-700">
                    {{ selectedClient?.cnpj || "00.000.000/0001-00" }}
                  </span>
                </div>
              </div>
            </div>

            <!-- posição -->
            <div
              class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
            >
              <h3 class="text-base font-bold text-gray-800">
                Posição em percentual
              </h3>

              <p class="mt-1 text-sm text-gray-500">
                Ajuste a área selecionada usando valores de 0 a 100.
              </p>

              <p
                class="mt-2 rounded-xl bg-green-50 px-3 py-2 text-xs text-green-700"
              >
                Você também pode arrastar e redimensionar a área diretamente
                sobre a capa.
              </p>

              <div class="mt-5 grid grid-cols-2 gap-4">
                <div>
                  <label
                    class="mb-1.5 block text-sm font-semibold text-gray-700"
                  >
                    X
                  </label>

                  <input
                    v-model.number="currentArea.x"
                    type="number"
                    min="-100"
                    max="200"
                    step="0.5"
                    class="editor-input"
                    @change="normalizeCurrentArea"
                  />
                </div>

                <div>
                  <label
                    class="mb-1.5 block text-sm font-semibold text-gray-700"
                  >
                    Y
                  </label>

                  <input
                    v-model.number="currentArea.y"
                    type="number"
                    min="-100"
                    max="200"
                    step="0.5"
                    class="editor-input"
                    @change="normalizeCurrentArea"
                  />
                </div>

                <div>
                  <label
                    class="mb-1.5 block text-sm font-semibold text-gray-700"
                  >
                    Largura
                  </label>

                  <input
                    v-model.number="currentArea.width"
                    type="number"
                    min="1"
                    max="200"
                    step="0.5"
                    class="editor-input"
                    @change="normalizeCurrentArea"
                  />
                </div>

                <div>
                  <label
                    class="mb-1.5 block text-sm font-semibold text-gray-700"
                  >
                    Altura
                  </label>

                  <input
                    v-model.number="currentArea.height"
                    type="number"
                    min="1"
                    max="200"
                    step="0.5"
                    class="editor-input"
                    @change="normalizeCurrentArea"
                  />
                </div>
              </div>

              <div
                class="mt-5 rounded-2xl border border-gray-100 bg-gray-50 p-4"
              >
                <p class="text-xs font-semibold uppercase text-gray-400">
                  JSON gerado
                </p>

                <pre class="mt-2 overflow-auto text-xs text-gray-600">{{
                  areaJson
                }}</pre>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  </div>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  PhArrowCounterClockwise,
  PhArrowLeft,
  PhBuildings,
  PhCheck,
  PhCheckCircle,
  PhCircleNotch,
  PhEye,
  PhEyeSlash,
  PhImageSquare,
  PhStar,
  PhWarningCircle,
  PhPlus,
  PhTrash,
} from "@phosphor-icons/vue";

import PageTitleVue from "@/components/Titles/PageTitle.vue";
import { showToast as toast } from "@/helpers/UseToasts";

import ReportLayoutsService from "./services/reportLayouts.service";
import ReportVisitClientsService from "./services/reportVisitClients.service";

import ReportEmptyState from "./components/shared/ReportEmptyState.vue";
import protegeLogo from "@/assets/logo/protege.png";

const route = useRoute();
const router = useRouter();

const layout = ref(null);
const clients = ref([]);
const selectedClientId = ref("");
const selectedAreaKey = ref("logo_area");
const isSaving = ref(false);
const coverPreviewRef = ref(null);

const interaction = ref({
  active: false,
  mode: null,
  handle: null,
  areaKey: null,
  startX: 0,
  startY: 0,
  startArea: null,
  rect: null,
});

const resizeHandles = [
  {
    key: "nw",
    style: {
      left: "0%",
      top: "0%",
      cursor: "nwse-resize",
      transform: "translate(-50%, -50%)",
    },
  },
  {
    key: "n",
    style: {
      left: "50%",
      top: "0%",
      cursor: "ns-resize",
      transform: "translate(-50%, -50%)",
    },
  },
  {
    key: "ne",
    style: {
      left: "100%",
      top: "0%",
      cursor: "nesw-resize",
      transform: "translate(-50%, -50%)",
    },
  },
  {
    key: "e",
    style: {
      left: "100%",
      top: "50%",
      cursor: "ew-resize",
      transform: "translate(-50%, -50%)",
    },
  },
  {
    key: "se",
    style: {
      left: "100%",
      top: "100%",
      cursor: "nwse-resize",
      transform: "translate(-50%, -50%)",
    },
  },
  {
    key: "s",
    style: {
      left: "50%",
      top: "100%",
      cursor: "ns-resize",
      transform: "translate(-50%, -50%)",
    },
  },
  {
    key: "sw",
    style: {
      left: "0%",
      top: "100%",
      cursor: "nesw-resize",
      transform: "translate(-50%, -50%)",
    },
  },
  {
    key: "w",
    style: {
      left: "0%",
      top: "50%",
      cursor: "ew-resize",
      transform: "translate(-50%, -50%)",
    },
  },
];

const areas = reactive({});
const elementConfigs = reactive({});
const customFields = ref([]);

const editableAreas = [
  {
    key: "logo_area",
    label: "Logo da empresa",
    description: "Área onde a logo do cliente visitado será aplicada.",
    type: "image",
    storage: "column",
  },
  {
    key: "client_name_area",
    label: "Nome da empresa",
    description: "Área onde o nome fantasia ou razão social será exibido.",
    type: "text",
    storage: "column",
  },
  {
    key: "client_document_area",
    label: "CNPJ da empresa",
    description: "Área onde o documento da empresa será exibido.",
    type: "text",
    storage: "column",
  },
  {
    key: "visit_date_area",
    label: "Data da visita",
    description: "Área futura para exibir a data da visita técnica.",
    type: "text",
    storage: "settings",
  },
  {
    key: "technician_name_area",
    label: "Técnico responsável",
    description: "Área futura para exibir o nome do técnico responsável.",
    type: "text",
    storage: "settings",
  },
];

const editableElements = computed(() => {
  return [
    ...editableAreas,
    ...customFields.value.map((field) => ({
      key: field.key,
      id: field.id,
      label: field.label || "Campo personalizado",
      description: field.value || "Texto personalizado da capa.",
      type: "text",
      storage: "custom",
    })),
  ];
});

const fontFamilyOptions = [
  {
    label: "Arial",
    value: "Arial, Helvetica, sans-serif",
  },
  {
    label: "Helvetica",
    value: "Helvetica, Arial, sans-serif",
  },
  {
    label: "Verdana",
    value: "Verdana, Geneva, sans-serif",
  },
  {
    label: "Tahoma",
    value: "Tahoma, Geneva, sans-serif",
  },
  {
    label: "Georgia",
    value: "Georgia, serif",
  },
  {
    label: "Times New Roman",
    value: "'Times New Roman', Times, serif",
  },
  {
    label: "Courier New",
    value: "'Courier New', Courier, monospace",
  },
  {
    label: "Impact",
    value: "Impact, Charcoal, sans-serif",
  },
];

const logoShapeOptions = [
  {
    label: "Quadrado",
    value: "square",
  },
  {
    label: "Arredondado",
    value: "rounded",
  },
  {
    label: "Círculo",
    value: "circle",
  },
  {
    label: "Pílula",
    value: "pill",
  },
  {
    label: "Losango",
    value: "diamond",
  },
  {
    label: "Hexágono",
    value: "hexagon",
  },
];

const objectFitOptions = [
  {
    label: "Conter",
    value: "contain",
  },
  {
    label: "Preencher",
    value: "cover",
  },
  {
    label: "Esticar",
    value: "fill",
  },
];

const fontWeightOptions = [
  { label: "Regular", value: "400" },
  { label: "Médio", value: "500" },
  { label: "Semibold", value: "600" },
  { label: "Bold", value: "700" },
  { label: "Extra Bold", value: "800" },
  { label: "Black", value: "900" },
];

const textAlignOptions = [
  { label: "Esquerda", value: "left" },
  { label: "Centro", value: "center" },
  { label: "Direita", value: "right" },
];

const textTransformOptions = [
  { label: "Normal", value: "none" },
  { label: "Maiúsculo", value: "uppercase" },
  { label: "Minúsculo", value: "lowercase" },
  { label: "Capitalizado", value: "capitalize" },
];

const selectedArea = computed(() => {
  return (
    editableElements.value.find(
      (currentArea) => currentArea.key === selectedAreaKey.value
    ) || editableElements.value[0]
  );
});

const currentArea = computed(() => {
  return getAreaFromState(selectedAreaKey.value);
});

const currentElementConfig = computed(() => {
  return getElementConfigFromState(selectedAreaKey.value);
});

const currentElementStyle = computed(() => {
  return currentElementConfig.value.style;
});

const isCurrentAreaText = computed(() => {
  return selectedArea.value?.type === "text";
});

const isCurrentAreaImage = computed(() => {
  return selectedArea.value?.type === "image";
});

const isCurrentCustomField = computed(() => {
  return selectedArea.value?.storage === "custom";
});

const currentCustomField = computed(() => {
  if (!isCurrentCustomField.value) return null;

  return customFields.value.find(
    (field) => field.key === selectedAreaKey.value
  );
});

const coverSrc = computed(() => {
  if (!layout.value) return "";

  return getAssetUrl(layout.value, "cover");
});

const selectedClient = computed(() => {
  return clients.value.find(
    (client) => Number(client.id) === Number(selectedClientId.value)
  );
});

const testClientName = computed(() => {
  return (
    selectedClient.value?.trade_name ||
    selectedClient.value?.corporate_name ||
    "Empresa de Teste"
  );
});

const testLogoSrc = computed(() => {
  if (!selectedClient.value) return protegeLogo;

  return (
    selectedClient.value.logo_url ||
    buildFileUrl(selectedClient.value.logo_path) ||
    protegeLogo
  );
});

const areaJson = computed(() => {
  return JSON.stringify(normalizedArea(currentArea.value), null, 2);
});

const visiblePreviewAreas = computed(() => {
  if (!layout.value) return [];

  return editableElements.value
    .map((areaOption) => {
      const currentArea = getAreaFromState(areaOption.key);
      const config = getElementConfigFromState(areaOption.key);

      if (!currentArea || !config.visible) return null;

      return {
        ...areaOption,
        area: normalizedArea(currentArea),
        config,
      };
    })
    .filter(Boolean);
});

onMounted(async () => {
  await Promise.all([loadLayout(), loadClients()]);
});

async function loadLayout() {
  try {
    const response = await ReportLayoutsService.getById(route.params.id);

    layout.value = response?.data?.data || response?.data || null;

    hydrateAreasFromLayout();
  } catch (error) {
    console.error(error);

    toast("Protege informa:", "Não foi possível carregar o layout.", "error");

    goBack();
  }
}

async function loadClients() {
  try {
    const response = await ReportVisitClientsService.getAll({
      include_inactive: true,
    });

    clients.value =
      response?.data?.data || response?.data?.clients || response?.data || [];

    const firstClientWithLogo = clients.value.find(
      (client) => client.logo_url || client.logo_path
    );

    selectedClientId.value =
      firstClientWithLogo?.id || clients.value[0]?.id || "";
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível carregar as empresas para teste.",
      "error"
    );
  }
}

function hydrateAreasFromLayout() {
  resetEditorStateMaps();

  customFields.value = normalizeCustomFieldsFromLayout();

  editableElements.value.forEach((areaOption) => {
    const savedArea = getAreaFromLayout(areaOption.key);
    const fallbackArea = getDefaultArea(areaOption.key);

    areas[areaOption.key] = normalizedArea(savedArea || fallbackArea);

    elementConfigs[areaOption.key] = normalizedElementConfig(
      areaOption,
      getElementConfigFromLayout(areaOption.key)
    );
  });
}

function resetEditorStateMaps() {
  Object.keys(areas).forEach((key) => {
    delete areas[key];
  });

  Object.keys(elementConfigs).forEach((key) => {
    delete elementConfigs[key];
  });
}

function normalizeCustomFieldsFromLayout() {
  const savedFields = layout.value?.settings?.cover?.custom_fields || [];

  if (!Array.isArray(savedFields)) return [];

  return savedFields.map((field, index) => {
    const id = field.id || createCustomFieldId();
    const key = field.key || makeCustomFieldKey(id);

    const normalizedField = {
      id,
      key,
      label: field.label || `Campo personalizado ${index + 1}`,
      value: field.value || "Texto personalizado",
      type: "text",
      storage: "custom",
      description: "Campo personalizado da capa.",
    };

    normalizedField.area = normalizedArea(
      field.area || getDefaultArea(key)
    );

    normalizedField.config = normalizedElementConfig(
      normalizedField,
      field.config
    );

    return normalizedField;
  });
}

function createCustomFieldId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `custom_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function makeCustomFieldKey(id) {
  return `custom_field_${id}`;
}

function isCustomFieldKey(areaKey) {
  return String(areaKey || "").startsWith("custom_field_");
}

function getAreaFromLayout(areaKey) {
  if (!layout.value) return null;

  const areaOption = editableElements.value.find(
    (currentArea) => currentArea.key === areaKey
  );

  if (!areaOption) return null;

  if (areaOption.storage === "custom") {
    const field = customFields.value.find(
      (customField) => customField.key === areaKey
    );

    return field?.area || null;
  }

  if (areaOption.storage === "column") {
    return layout.value[areaKey] || null;
  }

  return layout.value.settings?.dynamic_areas?.[areaKey] || null;
}

function getAreaFromState(areaKey) {
  if (!areas[areaKey]) {
    areas[areaKey] = normalizedArea(getDefaultArea(areaKey));
  }

  return areas[areaKey];
}

function hasSavedArea(areaKey) {
  if (isCustomFieldKey(areaKey)) {
    return customFields.value.some((field) => field.key === areaKey);
  }

  return Boolean(areas[areaKey] || getAreaFromLayout(areaKey));
}

function normalizeCurrentArea() {
  const current = getAreaFromState(selectedAreaKey.value);

  Object.assign(current, normalizedArea(current));
}

function normalizeAreaByKey(areaKey) {
  const current = getAreaFromState(areaKey);

  Object.assign(current, normalizedArea(current));
}

function resetCurrentArea() {
  areas[selectedAreaKey.value] = normalizedArea(
    getDefaultArea(selectedAreaKey.value)
  );
}

function buildLayoutPayloadWithAreas() {
  const currentSettings = {
    ...(layout.value.settings || {}),
  };

  const dynamicAreas = {
    ...(currentSettings.dynamic_areas || {}),
  };

  const coverSettings = {
    ...(currentSettings.cover || {}),
  };

  const coverAreas = {
    ...(coverSettings.areas || {}),
  };

  const payload = {
    name: layout.value.name,
    description: layout.value.description,

    cover_path: layout.value.cover_path || null,
    cover_url: layout.value.cover_url || null,

    header_path: layout.value.header_path || null,
    header_url: layout.value.header_url || null,

    footer_path: layout.value.footer_path || null,
    footer_url: layout.value.footer_url || null,

    watermark_path: layout.value.watermark_path || null,
    watermark_url: layout.value.watermark_url || null,

    primary_color: layout.value.primary_color || null,
    secondary_color: layout.value.secondary_color || null,

    page_format: layout.value.page_format || "a4",
    orientation: layout.value.orientation || "portrait",

    logo_area: layout.value.logo_area || null,
    client_name_area: layout.value.client_name_area || null,
    client_document_area: layout.value.client_document_area || null,

    settings: currentSettings,

    is_default: Boolean(layout.value.is_default),
    is_active: Boolean(layout.value.is_active),
  };

  editableAreas.forEach((areaOption) => {
    const savedArea = normalizedArea(getAreaFromState(areaOption.key));
    const savedConfig = getElementConfigFromState(areaOption.key);

    if (areaOption.storage === "column") {
      payload[areaOption.key] = savedArea;
    } else {
      dynamicAreas[areaOption.key] = savedArea;
    }

    coverAreas[areaOption.key] = {
      ...(coverAreas[areaOption.key] || {}),
      visible: Boolean(savedConfig.visible),
      style: {
        ...savedConfig.style,
      },
    };
  });

  const customFieldsPayload = customFields.value.map((field) => {
    const savedConfig = getElementConfigFromState(field.key);

    return {
      id: field.id,
      key: field.key,
      label: field.label || "Campo personalizado",
      value: field.value || "",
      type: "text",
      area: normalizedArea(getAreaFromState(field.key)),
      config: {
        visible: Boolean(savedConfig.visible),
        style: {
          ...savedConfig.style,
        },
      },
    };
  });

  payload.settings = {
    ...currentSettings,
    dynamic_areas: dynamicAreas,
    cover: {
      ...coverSettings,
      areas: coverAreas,
      custom_fields: customFieldsPayload,
    },
  };

  return payload;
}

function selectArea(areaKey) {
  selectedAreaKey.value = areaKey;
  getAreaFromState(areaKey);
}

function getDefaultArea(areaKey) {
  if (isCustomFieldKey(areaKey)) {
    return {
      x: 10,
      y: 18,
      width: 55,
      height: 7,
      unit: "percent",
    };
  }

  const defaults = {
    logo_area: {
      x: 68,
      y: 10,
      width: 22,
      height: 12,
      unit: "percent",
    },
    client_name_area: {
      x: 10,
      y: 72,
      width: 55,
      height: 5,
      unit: "percent",
    },
    client_document_area: {
      x: 10,
      y: 78,
      width: 35,
      height: 4,
      unit: "percent",
    },
    visit_date_area: {
      x: 10,
      y: 84,
      width: 28,
      height: 4,
      unit: "percent",
    },
    technician_name_area: {
      x: 10,
      y: 89,
      width: 45,
      height: 4,
      unit: "percent",
    },
  };

  return {
    ...(defaults[areaKey] || defaults.logo_area),
  };
}

function normalizedArea(currentArea) {
  const safeArea = {
    x: Number(currentArea?.x ?? 0),
    y: Number(currentArea?.y ?? 0),
    width: Number(currentArea?.width ?? 1),
    height: Number(currentArea?.height ?? 1),
    unit: "percent",
  };

  safeArea.x = clamp(safeArea.x, -100, 200);
  safeArea.y = clamp(safeArea.y, -100, 200);
  safeArea.width = clamp(safeArea.width, 1, 200);
  safeArea.height = clamp(safeArea.height, 1, 200);

  return safeArea;
}

function clamp(value, min, max) {
  return Math.min(Math.max(Number(value || 0), min), max);
}

function getAreaStyle(currentArea) {
  return {
    left: `${currentArea.x}%`,
    top: `${currentArea.y}%`,
    width: `${currentArea.width}%`,
    height: `${currentArea.height}%`,
  };
}

function getTextPreview(areaKey) {
  const customField = customFields.value.find(
    (field) => field.key === areaKey
  );

  if (customField) {
    return customField.value || "Texto personalizado";
  }

  const previews = {
    client_name_area: testClientName.value,
    client_document_area: selectedClient.value?.cnpj || "00.000.000/0001-00",
    visit_date_area: "05/05/2026",
    technician_name_area: "Técnico Responsável",
  };

  return previews[areaKey] || "Texto dinâmico";
}

async function saveCurrentArea() {
  if (!layout.value) return;

  isSaving.value = true;

  try {
    const payload = buildLayoutPayloadWithAreas();

    const response = await ReportLayoutsService.update(
      layout.value.id,
      payload
    );

    layout.value = response?.data?.data || response?.data || layout.value;

    hydrateAreasFromLayout();

    toast(
      "Protege informa:",
      "Configurações do layout salvas com sucesso.",
      "success"
    );
  } catch (error) {
    console.error(error);

    toast(
      "Protege informa:",
      "Não foi possível salvar as configurações do layout.",
      "error"
    );
  } finally {
    isSaving.value = false;
  }
}

function getAssetUrl(currentLayout, type) {
  const url = currentLayout[`${type}_url`];

  if (url) return url;

  const path = currentLayout[`${type}_path`];

  return buildFileUrl(path);
}

function buildFileUrl(path) {
  if (!path) return "";

  const baseUrl =
    process.env.VUE_APP_FILES_URL ||
    process.env.VUE_APP_BASE_FILES_URL ||
    process.env.VUE_APP_IMAGE_SERVE ||
    "";

  if (!baseUrl) return "";

  return `${baseUrl.replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
}

function startMoveArea(areaKey, event) {
  selectArea(areaKey);

  const rect = coverPreviewRef.value?.getBoundingClientRect();

  if (!rect) return;

  interaction.value = {
    active: true,
    mode: "move",
    handle: null,
    areaKey,
    startX: event.clientX,
    startY: event.clientY,
    startArea: { ...normalizedArea(getAreaFromState(areaKey)) },
    rect,
  };

  startDocumentInteraction("move");
}

function startResizeArea(areaKey, handle, event) {
  selectArea(areaKey);

  const rect = coverPreviewRef.value?.getBoundingClientRect();

  if (!rect) return;

  interaction.value = {
    active: true,
    mode: "resize",
    handle,
    areaKey,
    startX: event.clientX,
    startY: event.clientY,
    startArea: { ...normalizedArea(getAreaFromState(areaKey)) },
    rect,
  };

  startDocumentInteraction("resize", handle);
}

function startDocumentInteraction(mode, handle = null) {
  document.addEventListener("pointermove", handlePointerMove);
  document.addEventListener("pointerup", stopDocumentInteraction);
  document.addEventListener("pointercancel", stopDocumentInteraction);

  document.body.style.userSelect = "none";

  if (mode === "move") {
    document.body.style.cursor = "move";
  }

  if (mode === "resize") {
    document.body.style.cursor = getResizeCursor(handle);
  }
}

function stopDocumentInteraction() {
  const areaKey = interaction.value.areaKey;

  document.removeEventListener("pointermove", handlePointerMove);
  document.removeEventListener("pointerup", stopDocumentInteraction);
  document.removeEventListener("pointercancel", stopDocumentInteraction);

  document.body.style.userSelect = "";
  document.body.style.cursor = "";

  if (areaKey) {
    normalizeAreaByKey(areaKey);
  }

  interaction.value = {
    active: false,
    mode: null,
    handle: null,
    areaKey: null,
    startX: 0,
    startY: 0,
    startArea: null,
    rect: null,
  };

  // if (interaction.value.areaKey) {
  //   normalizeAreaByKey(interaction.value.areaKey);
  // }
}

function handlePointerMove(event) {
  const currentInteraction = interaction.value;

  if (!currentInteraction.active || !currentInteraction.rect) return;

  const deltaX =
    ((event.clientX - currentInteraction.startX) /
      currentInteraction.rect.width) *
    100;
  const deltaY =
    ((event.clientY - currentInteraction.startY) /
      currentInteraction.rect.height) *
    100;

  if (currentInteraction.mode === "move") {
    applyMove(deltaX, deltaY);
    return;
  }

  if (currentInteraction.mode === "resize") {
    applyResize(deltaX, deltaY);
  }
}

function applyMove(deltaX, deltaY) {
  const startArea = interaction.value.startArea;
  const areaKey = interaction.value.areaKey;

  if (!startArea || !areaKey) return;

  const nextArea = {
    ...startArea,
    x: clamp(startArea.x + deltaX, -100, 200),
    y: clamp(startArea.y + deltaY, -100, 200),
  };

  Object.assign(getAreaFromState(areaKey), normalizedArea(nextArea));
}

function applyResize(deltaX, deltaY) {
  const startArea = interaction.value.startArea;
  const handle = interaction.value.handle;
  const areaKey = interaction.value.areaKey;

  if (!startArea || !handle || !areaKey) return;

  const minSize = 1;

  let nextX = startArea.x;
  let nextY = startArea.y;
  let nextWidth = startArea.width;
  let nextHeight = startArea.height;

  if (handle.includes("e")) {
    nextWidth = startArea.width + deltaX;
  }

  if (handle.includes("w")) {
    nextX = startArea.x + deltaX;
    nextWidth = startArea.width - deltaX;
  }

  if (handle.includes("s")) {
    nextHeight = startArea.height + deltaY;
  }

  if (handle.includes("n")) {
    nextY = startArea.y + deltaY;
    nextHeight = startArea.height - deltaY;
  }

  if (nextWidth < minSize) {
    if (handle.includes("w")) {
      nextX = startArea.x + startArea.width - minSize;
    }

    nextWidth = minSize;
  }

  if (nextHeight < minSize) {
    if (handle.includes("n")) {
      nextY = startArea.y + startArea.height - minSize;
    }

    nextHeight = minSize;
  }

  Object.assign(
    getAreaFromState(areaKey),
    normalizedArea({
      x: nextX,
      y: nextY,
      width: nextWidth,
      height: nextHeight,
      unit: "percent",
    })
  );
}

function getResizeCursor(handle) {
  const cursors = {
    nw: "nwse-resize",
    n: "ns-resize",
    ne: "nesw-resize",
    e: "ew-resize",
    se: "nwse-resize",
    s: "ns-resize",
    sw: "nesw-resize",
    w: "ew-resize",
  };

  return cursors[handle] || "default";
}

function goBack() {
  router.push({
    name: "reports-home",
  });
}

function getElementConfigFromLayout(areaKey) {
  if (!layout.value) return null;

  const areaOption = editableElements.value.find(
    (currentArea) => currentArea.key === areaKey
  );

  if (areaOption?.storage === "custom") {
    const field = customFields.value.find(
      (customField) => customField.key === areaKey
    );

    return field?.config || null;
  }

  return layout.value.settings?.cover?.areas?.[areaKey] || null;
}

function getElementConfigFromState(areaKey) {
  const areaOption = editableAreas.find((area) => area.key === areaKey);

  if (!elementConfigs[areaKey]) {
    elementConfigs[areaKey] = normalizedElementConfig(
      areaOption,
      getElementConfigFromLayout(areaKey)
    );
  }

  return elementConfigs[areaKey];
}

function normalizedElementConfig(areaOption, config = null) {
  const defaultConfig = getDefaultElementConfig(areaOption);

  return {
    visible:
      typeof config?.visible === "boolean"
        ? config.visible
        : defaultConfig.visible,

    style: {
      ...defaultConfig.style,
      ...(config?.style || {}),
    },
  };
}

function getDefaultElementConfig(areaOption) {
  if (areaOption?.type === "image") {
    return {
      visible: true,
      style: {
        opacity: 1,
        shape: "square",
        objectFit: "contain",
        padding: 4,
        backgroundColor: "transparent",
        borderWidth: 0,
        borderColor: "#ffffff",
      },
    };
  }

  return {
    visible: true,
    style: {
      fontFamily: "Arial, Helvetica, sans-serif",
      fontSize: getDefaultFontSize(areaOption?.key),
      fontWeight: "700",
      color: layout.value?.primary_color || "#111827",
      textAlign: "center",
      lineHeight: 1.15,
      letterSpacing: 0,
      textTransform: "none",
      opacity: 1,
    },
  };
}

function getDefaultFontSize(areaKey) {
  if (isCustomFieldKey(areaKey)) {
    return 28;
  }

  const sizes = {
    client_name_area: 20,
    client_document_area: 12,
    visit_date_area: 12,
    technician_name_area: 12,
  };

  return sizes[areaKey] || 14;
}

function isAreaVisible(areaKey) {
  return getElementConfigFromState(areaKey).visible;
}

function toggleAreaVisibility(areaKey) {
  const config = getElementConfigFromState(areaKey);

  config.visible = !config.visible;
}

function normalizeCurrentElementStyle() {
  const style = currentElementStyle.value;

  style.opacity = clamp(style.opacity, 0, 1);

  if (isCurrentAreaText.value) {
    style.fontSize = clamp(style.fontSize, 6, 120);
    style.lineHeight = clamp(style.lineHeight, 0.7, 3);
    style.letterSpacing = clamp(style.letterSpacing, -10, 40);
  }

  if (isCurrentAreaImage.value) {
    style.padding = clamp(style.padding, 0, 80);
    style.borderWidth = clamp(style.borderWidth, 0, 40);

    if (!style.shape) {
      style.shape = "square";
    }

    if (!style.objectFit) {
      style.objectFit = "contain";
    }

    if (!style.backgroundColor) {
      style.backgroundColor = "transparent";
    }

    if (!style.borderColor) {
      style.borderColor = "#ffffff";
    }
  }
}

function getTextAreaStyle(previewArea) {
  const style = previewArea.config?.style || {};

  return {
    width: "100%",
    color: style.color || layout.value?.primary_color || "#111827",
    fontFamily: style.fontFamily || "Arial, Helvetica, sans-serif",
    fontSize: `${style.fontSize || 14}px`,
    fontWeight: style.fontWeight || "700",
    textAlign: style.textAlign || "center",
    lineHeight: String(style.lineHeight || 1.15),
    letterSpacing: `${style.letterSpacing || 0}px`,
    textTransform: style.textTransform || "none",
    opacity: String(style.opacity ?? 1),
  };
}

function getAreaContentFrameStyle(previewArea) {
  if (previewArea.type !== "image") {
    return {};
  }

  const style = previewArea.config?.style || {};
  const shape = style.shape || "square";

  return {
    padding: `${Number(style.padding || 0)}px`,
    backgroundColor: style.backgroundColor || "transparent",
    border: `${Number(style.borderWidth || 0)}px solid ${
      style.borderColor || "transparent"
    }`,
    borderRadius: getShapeBorderRadius(shape),
    clipPath: getShapeClipPath(shape),
    opacity: String(style.opacity ?? 1),
  };
}

function getImageAreaStyle(previewArea) {
  const style = previewArea.config?.style || {};

  return {
    objectFit: style.objectFit || "contain",
  };
}

function getShapeBorderRadius(shape) {
  const radiusMap = {
    square: "0",
    rounded: "18px",
    circle: "9999px",
    pill: "9999px",
    diamond: "0",
    hexagon: "0",
  };

  return radiusMap[shape] || "0";
}

function getShapeClipPath(shape) {
  const clipPathMap = {
    square: "none",
    rounded: "none",
    circle: "none",
    pill: "none",
    diamond: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
    hexagon:
      "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
  };

  return clipPathMap[shape] || "none";
}

function addCustomField() {
  const id = createCustomFieldId();
  const key = makeCustomFieldKey(id);
  const number = customFields.value.length + 1;

  const field = {
    id,
    key,
    label: `Campo personalizado ${number}`,
    value: "Texto personalizado",
    type: "text",
    storage: "custom",
    description: "Campo personalizado da capa.",
  };

  customFields.value.push(field);

  areas[key] = normalizedArea({
    x: 10,
    y: 15 + number * 7,
    width: 55,
    height: 8,
    unit: "percent",
  });

  elementConfigs[key] = normalizedElementConfig(field, null);

  selectedAreaKey.value = key;
}

function removeCustomField(areaKey) {
  const field = customFields.value.find(
    (customField) => customField.key === areaKey
  );

  if (!field) return;

  const confirmRemove = window.confirm(
    `Deseja remover o campo personalizado "${field.label}"?`
  );

  if (!confirmRemove) return;

  customFields.value = customFields.value.filter(
    (customField) => customField.key !== areaKey
  );

  delete areas[areaKey];
  delete elementConfigs[areaKey];

  if (selectedAreaKey.value === areaKey) {
    selectedAreaKey.value = "logo_area";
  }
}

onBeforeUnmount(() => {
  stopDocumentInteraction();
});
</script>

<style scoped>
.editor-input {
  @apply w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none transition placeholder:text-gray-300 focus:border-[#2f8f2f] focus:ring-2 focus:ring-green-100;
}
</style>
